import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import { authService } from '../services/authService';

const initialState = {
  isAuthenticated: false,
  currentUser: null,
  status: 'loading',
};

const AuthContext = createContext({
  ...initialState,
  refreshUser: async () => {},
  setUserFromCallback: () => {},
});

const reducer = (state, action) => {
  switch (action.type) {
    case 'AUTH_LOADING':
      return { ...state, status: 'loading' };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        status: 'authenticated',
        isAuthenticated: true,
        currentUser: action.payload,
      };
    case 'AUTH_LOGOUT':
      return { ...initialState, status: 'unauthenticated' };
    case 'AUTH_ERROR':
      return { ...state, status: 'error' };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const refreshUser = useCallback(async () => {
    dispatch({ type: 'AUTH_LOADING' });
    try {
      const user = await authService.getCurrentUser();
      if (user) {
        dispatch({ type: 'AUTH_SUCCESS', payload: user });
      } else {
        dispatch({ type: 'AUTH_LOGOUT' });
      }
    } catch (error) {
      console.error('Failed to refresh auth state', error);
      dispatch({ type: 'AUTH_ERROR' });
    }
  }, []);

  const setUserFromCallback = useCallback((user) => {
    if (user) {
      dispatch({ type: 'AUTH_SUCCESS', payload: authService.normalizeUser(user) });
    } else {
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const signinResult = params.get('signin');
    if (signinResult) {
      params.delete('signin');
      const nextQuery = params.toString();
      const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', nextUrl);
    }

    if (signinResult === 'error') {
      dispatch({ type: 'AUTH_ERROR' });
      return;
    }

    refreshUser();
  }, [refreshUser]);

  const value = useMemo(() => ({
    ...state,
    refreshUser,
    setUserFromCallback,
  }), [state, refreshUser, setUserFromCallback]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
